import type { ChangeEvent } from 'react';

import { Heading, SelectionInputLayout, Toggle, classNames } from '@movingimage-evp/mi-ui-component-library';
import { useTranslation } from 'react-i18next';

import {
  useGetLsproDefaultSettingsQuery,
  useUpdateLsproDefaultSettingsMutation,
} from '../../../../generated/graphql-manager';
import { useCurrentUser } from '../../../hooks/current-user';

import managerStyles from '../../../manager.module.css';
import styles from '../channel-settings.module.css';

export function AccessPermissionPage() {
  const { lsproId } = useCurrentUser();
  const { t } = useTranslation();

  const { data } = useGetLsproDefaultSettingsQuery({ variables: { lsproId } });

  const defaultSettings = data?.lsproSettings?.defaultSettings;

  const [updateLsproDefaultSettings] = useUpdateLsproDefaultSettingsMutation();

  const handleDefaultSettingsChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;

    updateLsproDefaultSettings({
      variables: {
        input: {
          lsproId,
          [name]: checked,
        },
      },
      optimisticResponse: {
        __typename: 'Mutation',
        updateLiveStreamProDefaultSettings: {
          __typename: 'UpdateLiveStreamProDefaultSettingsSuccess',
          defaultSettings: {
            __typename: 'LiveStreamProDefaultSettings',
            ...defaultSettings,
            allowTranscodingProfileSelection: defaultSettings?.allowTranscodingProfileSelection ?? false,
            privateEventAsDefault: defaultSettings?.privateEventAsDefault ?? false,
            restrictAttendanceReport: defaultSettings?.restrictAttendanceReport ?? false,
            singleSignOnAsDefault: defaultSettings?.singleSignOnAsDefault ?? false,
            showWatermarkingAsDefault: defaultSettings?.showWatermarkingAsDefault ?? false,
            defaultIngestSettings: defaultSettings?.defaultIngestSettings ?? {
              __typename: 'DefaultRtmpIngestSettings',
              authenticationEnabled: false,
            },
            [name]: checked,
          },
        },
      },
      update: (cache) => {
        cache.modify({
          id: cache.identify({ __typename: 'LiveStreamProSettings', lsproId }),
          fields: {
            defaultSettings: (existingDefaultSettings = {}) => ({
              ...existingDefaultSettings,
              [name]: checked,
            }),
          },
        });
      },
    });
  };

  return (
    <main className={classNames(managerStyles.main, styles.wrapper)} data-testid="access-permission-page">
      <Heading className={managerStyles.grayText}>{t('manager.channelSettings.accessPermission.heading')}</Heading>

      {defaultSettings?.singleSignOnAsDefault !== undefined && (
        <SelectionInputLayout
          hintMessage={t('manager.channelSettings.settings.sso.description')}
          checked={defaultSettings?.singleSignOnAsDefault}
          variant="ghost"
          inputElement={
            <Toggle
              data-testid="default-settings-sso-toggle"
              name="singleSignOnAsDefault"
              checked={defaultSettings?.singleSignOnAsDefault}
              onChange={handleDefaultSettingsChange}
            >
              {t('manager.channelSettings.settings.sso.label')}
            </Toggle>
          }
        />
      )}

      {defaultSettings?.privateEventAsDefault !== undefined && (
        <SelectionInputLayout
          hintMessage={t('manager.channelSettings.settings.privetEvent.description')}
          checked={defaultSettings?.privateEventAsDefault}
          variant="ghost"
          inputElement={
            <Toggle
              data-testid="default-settings-private-event-toggle"
              name="privateEventAsDefault"
              checked={defaultSettings?.privateEventAsDefault}
              onChange={handleDefaultSettingsChange}
            >
              {t('manager.channelSettings.settings.privetEvent.label')}
            </Toggle>
          }
        />
      )}

      {defaultSettings?.restrictAttendanceReport !== undefined && (
        <SelectionInputLayout
          checked={defaultSettings?.restrictAttendanceReport}
          variant="ghost"
          inputElement={
            <Toggle
              data-testid="default-settings-restrict-attendance-report-toggle"
              name="restrictAttendanceReport"
              checked={defaultSettings?.restrictAttendanceReport}
              onChange={handleDefaultSettingsChange}
            >
              {t('manager.channelSettings.settings.restrictAttendanceReport.label')}
            </Toggle>
          }
        />
      )}
    </main>
  );
}
